import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AnalyticsPeople } from 'src/domain/analytics';
import { AdminPermission } from 'src/domain/admin';
import { ScrollableModal } from 'src/components/Modals/ScrollableModal';
import { useSignedInUser } from 'src/view/hooks/useSignedInUser';
import { useEnterSupportMode } from 'src/view/hooks/useEnterSupportMode';
import { useAnalyticsScope } from './analyticsScope';

type Population = 'employees' | 'residents';
type ChooserOrganization = Pick<
    AnalyticsPeople['organizations'][number],
    'id' | 'name'
>;

/** With counts, the organizations needing the most attention come first. */
const chooserOrganizations = (
    organizations: ChooserOrganization[],
    counts?: Record<string, number>,
) =>
    counts
        ? [...organizations].sort(
              (a, b) => (counts[b.id] ?? 0) - (counts[a.id] ?? 0),
          )
        : organizations;

/** A portal page opened in support mode, optionally with per-organization counts in the chooser. */
export interface DrilldownTarget {
    path: string;
    noteKey: string;
    counts?: Record<string, number>;
}

const INVITED_TARGETS: Record<Population, DrilldownTarget> = {
    employees: {
        path: '/app/creators?status=invited',
        noteKey: 'admin.analytics.openInvitedInSupport',
    },
    residents: {
        path: '/app/users?status=invited',
        noteKey: 'admin.analytics.openInvitedInSupport',
    },
};

export const useAnalyticsSupportDrilldown = (
    organizations: ChooserOrganization[],
) => {
    const { t, i18n } = useTranslation();
    const user = useSignedInUser();
    const scope = useAnalyticsScope();
    const history = useHistory();
    const organizationScope = scope.kind === 'organization';
    // Inside an organization the lists are already in reach; globally they open in support mode.
    const canOpen = organizationScope
        ? scope.canOpenLinks
        : Boolean(
              (
                  user as unknown as { adminPermissions?: AdminPermission[] }
              )?.adminPermissions?.includes(AdminPermission.support),
          );
    const { enterSupport, isEnteringSupport } = useEnterSupportMode();
    const [target, setTarget] = useState<DrilldownTarget>();
    const enter = (id: string, next: DrilldownTarget) =>
        enterSupport(id, next.path);
    const open = (requested: Population | DrilldownTarget) => {
        if (!canOpen || isEnteringSupport || !organizations.length) return;
        const next =
            typeof requested === 'string'
                ? INVITED_TARGETS[requested]
                : requested;
        if (organizationScope) {
            history.push(next.path);
        } else if (organizations.length === 1) {
            void enter(organizations[0].id, next);
        } else {
            setTarget(next);
        }
    };
    return {
        open,
        canOpen: canOpen && !isEnteringSupport && organizations.length > 0,
        chooser: target ? (
            <ScrollableModal
                visible
                onClose={() => {
                    if (!isEnteringSupport) setTarget(undefined);
                }}
            >
                <div
                    className="analytics-drilldown-dialog"
                    role="dialog"
                    aria-modal="true"
                    aria-label={t(
                        'admin.analytics.chooseDrilldownOrganization',
                    )}
                    aria-busy={isEnteringSupport}
                >
                    <h3>{t('admin.analytics.chooseDrilldownOrganization')}</h3>
                    <p className="analytics-note">{t(target.noteKey)}</p>
                    <div className="analytics-drilldown-organizations">
                        {chooserOrganizations(organizations, target.counts).map(
                            org => {
                                const count = target.counts?.[org.id];
                                return (
                                    <button
                                        type="button"
                                        key={org.id}
                                        disabled={isEnteringSupport}
                                        className={
                                            count === 0
                                                ? 'analytics-drilldown-empty'
                                                : undefined
                                        }
                                        onClick={() =>
                                            void enter(org.id, target)
                                        }
                                    >
                                        <span className="analytics-drilldown-name">
                                            {org.name}
                                        </span>
                                        {count !== undefined && (
                                            <span className="analytics-drilldown-count">
                                                {count.toLocaleString(
                                                    i18n.language,
                                                )}
                                            </span>
                                        )}
                                        <ChevronRight
                                            size={18}
                                            aria-hidden="true"
                                        />
                                    </button>
                                );
                            },
                        )}
                    </div>
                </div>
            </ScrollableModal>
        ) : null,
    };
};
